import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken, formatApiError } from "@/lib/api";
import { money, MONTHS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const RB = { pending: "text-orange-600 bg-orange-50", approved: "text-emerald-600 bg-emerald-50", rejected: "text-red-600 bg-red-50", paid: "text-blue-600 bg-blue-50" };

export default function EmployeeSalary() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", category: "Travel", amount: "", description: "" });
  const { data: payrolls } = useQuery({ queryKey: ["my-payroll"], queryFn: async () => (await api.get("/payroll/my")).data });
  const { data: history } = useQuery({ queryKey: ["my-salary-history"], queryFn: async () => (await api.get("/my/salary-history")).data });
  const { data: advances } = useQuery({ queryKey: ["my-advances"], queryFn: async () => (await api.get("/advances/my")).data });
  const { data: reimb } = useQuery({ queryKey: ["my-reimb"], queryFn: async () => (await api.get("/reimbursements/my")).data });

  const openPayslip = (id) => window.open(`${API}/payroll/${id}/payslip?auth=${getToken()}`, "_blank");

  const submitReimb = async () => {
    try {
      await api.post("/reimbursements", { ...form, amount: Number(form.amount) });
      toast.success("Expense claim submitted for approval");
      setOpen(false);
      setForm({ date: "", category: "Travel", amount: "", description: "" });
      qc.invalidateQueries({ queryKey: ["my-reimb"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 wp-fade-up">
      <h1 className="text-2xl font-head font-extrabold text-slate-900">Salary & Payslips</h1>

      <div>
        <h3 className="font-head font-bold text-slate-800 mb-2">Payslip History</h3>
        <div className="space-y-2">
          {(payrolls || []).length === 0 && <p className="text-sm text-slate-400">No payroll records yet.</p>}
          {(payrolls || []).map((p) => (
            <div key={p.id} data-testid={`payslip-row-${p.year}-${p.month}`} className="bg-white rounded-xl p-4 border border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">{MONTHS[p.month - 1]} {p.year}</div>
                <div className="text-sm text-slate-500">
                  Net Salary <span className="font-semibold text-slate-700">{money(p.calc?.net_payable)}</span>
                  {(p.reimbursement_total || 0) > 0 && <> + Reimb {money(p.reimbursement_total)} = <span className="font-semibold text-slate-700">{money(p.final_payable ?? p.calc?.net_payable)}</span></>}
                  {" · "}<span className={p.payment_status === "paid" ? "text-emerald-600" : "text-orange-600"}>{p.payment_status}</span>
                </div>
              </div>
              {p.locked && (
                <button data-testid={`download-payslip-${p.id}`} onClick={() => openPayslip(p.id)} className="flex items-center gap-1.5 text-sm font-medium text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                  <Download className="h-4 w-4" /> PDF
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Expense Reimbursements */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-head font-bold text-slate-800 flex items-center gap-2"><Receipt className="h-4 w-4 text-blue-600" /> Expense Reimbursements</h3>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-reimbursement-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Claim</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Expense Claim</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><label className="text-xs text-slate-500">Date</label><Input data-testid="reimb-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500">Category</label><Input data-testid="reimb-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Travel, Food, Supplies…" /></div>
                <div><label className="text-xs text-slate-500">Amount</label><Input data-testid="reimb-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                <Textarea data-testid="reimb-desc-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <Button data-testid="submit-reimbursement-button" onClick={submitReimb} disabled={!form.date || !form.amount} className="w-full bg-blue-600 hover:bg-blue-700">Submit Claim</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-2">
          {(reimb || []).length === 0 && <p className="text-sm text-slate-400">No expense claims yet.</p>}
          {(reimb || []).map((r) => (
            <div key={r.id} data-testid={`my-reimb-${r.id}`} className="bg-white rounded-xl p-4 border border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">{money(r.amount)} · {r.category}</div>
                <div className="text-xs text-slate-500">{r.date} · {r.description}</div>
              </div>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${RB[r.status]}`}>{r.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-head font-bold text-slate-800 mb-2">Advance Outstanding</h3>
        <div className="space-y-2">
          {(advances || []).length === 0 && <p className="text-sm text-slate-400">No advances.</p>}
          {(advances || []).map((a) => (
            <div key={a.id} className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">{a.advance_date} · {a.reason}</span>
                <span className="font-semibold">{money(a.outstanding)}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">Original {money(a.original_amount)} · Recovered {money(a.total_recovered)}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-head font-bold text-slate-800 mb-2">Salary History</h3>
        <div className="space-y-2">
          {(history || []).map((h) => (
            <div key={h.id} className="bg-white rounded-xl p-3 border border-slate-200 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{h.effective_from}</span>
                <span className="font-semibold">{money(h.previous_salary)} → {money(h.new_salary)}</span>
              </div>
              <div className="text-xs text-slate-400">{h.reason}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
